import { resolveColorFillCss } from './colorFillUtils';

export const HOVER_EFFECT_OPTIONS = [
  { value: 'none', label: 'Aucun' },
  { value: 'lighten-bg', label: 'Fond éclairci au survol' },
  { value: 'darken-bg', label: 'Fond assombri au survol' },
  { value: 'brighten', label: 'Texte éclairci' },
  { value: 'underline', label: 'Soulignement' },
  { value: 'underline-slide', label: 'Soulignement animé' },
  { value: 'highlight', label: 'Surbrillance (fond)' },
  { value: 'glow', label: 'Lueur' },
  { value: 'scale', label: 'Agrandissement léger' },
  { value: 'zoom', label: 'Zoom avant' },
  { value: 'lift', label: 'Élévation' },
  { value: 'shadow', label: 'Ombre portée' },
  { value: 'overlay', label: 'Assombrir (overlay)' },
];

/** Options recommandées pour les onglets du menu (couleur / éclaircissement). */
export const MENU_TAB_HOVER_OPTIONS = [
  { value: 'none', label: 'Aucun' },
  { value: 'lighten-bg', label: 'Fond éclairci' },
  { value: 'darken-bg', label: 'Fond assombri' },
  { value: 'brighten', label: 'Texte plus clair' },
  { value: 'highlight', label: 'Surbrillance' },
  { value: 'underline', label: 'Soulignement' },
  { value: 'underline-slide', label: 'Soulignement animé' },
  { value: 'glow', label: 'Lueur' },
  { value: 'scale', label: 'Agrandissement léger' },
  { value: 'lift', label: 'Élévation' },
  { value: 'shadow', label: 'Ombre portée' },
];

function clampIntensity(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 65;
  return Math.max(10, Math.min(100, n));
}

export function hoverEffectUsesWrap(effect) {
  return effect === 'zoom' || effect === 'brighten' || effect === 'overlay';
}

export function buildHoverEffectFromValues(effectRaw, intensityRaw, mode = 'wrap') {
  const effect = effectRaw || 'none';
  const intensity = clampIntensity(intensityRaw);
  const t = intensity / 100;

  if (effect === 'none') {
    return {
      effect: 'none',
      wrapClass: '',
      targetClass: '',
      selfClass: '',
      style: {},
    };
  }

  const scale = 1 + t * 0.14;
  const lift = -2 - t * 8;
  const shadowAlpha = 0.12 + t * 0.28;
  const brighten = 1 + t * 0.18;
  const overlay = 0.15 + t * 0.45;

  const style = {
    '--wc-hover-scale': String(scale),
    '--wc-hover-lift': `${lift}px`,
    '--wc-hover-shadow': `0 ${8 + t * 16}px ${20 + t * 24}px rgba(0,0,0,${shadowAlpha.toFixed(2)})`,
    '--wc-hover-brighten': String(brighten),
    '--wc-hover-overlay': String(overlay.toFixed(2)),
    '--wc-hover-intensity': String(t),
  };

  const fxClass = `wc-hover-fx wc-hover-fx--${effect}`;
  const useWrap = mode === 'wrap' || hoverEffectUsesWrap(effect);

  if (useWrap && mode !== 'self') {
    return {
      effect,
      wrapClass: `${fxClass} wc-hover-fx--wrap`,
      targetClass: 'wc-hover-fx-target',
      selfClass: '',
      style,
    };
  }

  return {
    effect,
    wrapClass: '',
    targetClass: '',
    selfClass: `${fxClass} wc-hover-fx--self`,
    style,
  };
}

/** Effet au survol via props (`imageHoverEffect`, `buttonHoverEffect`, …). */
export function buildHoverEffectPresentation(props, prefix = 'image') {
  const effect = props?.[`${prefix}HoverEffect`] || 'none';
  const intensity = props?.[`${prefix}HoverIntensity`];
  let mode = 'self';
  if (prefix === 'image' || (prefix === 'content' && hoverEffectUsesWrap(effect))) {
    mode = 'wrap';
  }
  return buildHoverEffectFromValues(effect, intensity, mode);
}

function menuTextIsLight(textColorFill) {
  if (!textColorFill) return false;
  const css =
    typeof textColorFill === 'string'
      ? textColorFill
      : resolveColorFillCss(textColorFill, '#111827');
  if (!css || String(css).includes('gradient')) return false;
  return isCssColorLight(css);
}

function isCssColorLight(cssColor) {
  const rgb = parseSimpleRgb(cssColor);
  if (!rgb) return false;
  const lum = relativeLuminance(rgb.r, rgb.g, rgb.b);
  return lum > 0.62;
}

function parseSimpleRgb(input) {
  const s = String(input || '').trim();
  const hex = s.match(/^#([0-9a-f]{3,8})$/i);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length >= 6) {
      return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
      };
    }
  }
  const rgb = s.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (rgb) {
    return { r: Number(rgb[1]), g: Number(rgb[2]), b: Number(rgb[3]) };
  }
  return null;
}

function relativeLuminance(r, g, b) {
  const chan = (c) => {
    const x = c / 255;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * chan(r) + 0.7152 * chan(g) + 0.0722 * chan(b);
}

export function resolveMenuTabHoverPresentation(menuProps, tabKey, { textColor } = {}) {
  const cfg = menuProps?.tabHoverEffects?.[tabKey];
  if (cfg && Object.prototype.hasOwnProperty.call(cfg, 'effect')) {
    return buildHoverEffectFromValues(cfg.effect, cfg.intensity, 'self');
  }
  const effect = menuTextIsLight(textColor) ? 'lighten-bg' : 'darken-bg';
  return buildHoverEffectFromValues(effect, 50, 'self');
}
